import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import QRCode from "qrcode.react";
import {
  ArrowDownTrayIcon,
  CheckIcon,
  PrinterIcon,
} from "@heroicons/react/20/solid";
import { axiosInstance } from "../../utils/axios";
import html2pdf from "html-to-pdf-js";
import { useReactToPrint } from "react-to-print"; // Import the react-to-print library
import Ritex from "../../assets/Ritex.png";
import dayjs from "dayjs";
// import { toPdf } from 'html-to-pdf-js';

function DeviceDetails({ deviceID, printOnLoad: propPrintOnLoad }) {
  const [searchParams] = useSearchParams();

  // Get printOnLoad from URL params or props
  const urlPrintOnLoad = searchParams.get("printOnLoad") === "true";
  const printOnLoad = propPrintOnLoad || urlPrintOnLoad;
  const [deviceData, setDeviceData] = useState(null);
  const [nextUniqueId, setNextUniqueId] = useState(null);
  const [error, setError] = useState(null);
  const componentRef = useRef(null);

  useEffect(() => {
    if (printOnLoad && deviceData) {
      // Check if deviceData is not null
      handlePrint();
    }
  }, [printOnLoad, deviceData]);

  useEffect(() => {
    fetchDevice();
  }, []);

  const fetchDevice = async () => {
    try {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/devices/${deviceID}`
      );
      setDeviceData(response.data.device);
      setNextUniqueId(response.data.next_unique_id);
      setError(null);
    } catch (error) {
      setError("Failed to fetch device. Please try again.");
      console.error("Fetch device error:", error);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page {
        size: A4;
        min-width: 100%;
        width: 100%; /* Set the width to 100% for the printed page */
      }
      body {
        min-width: 100%;
        width: 100%;
        padding: 0;
        margin: 0; /* Reset margins for the printed page */
      }
      #device-details{
        overflow: hidden;
        box-sizing: border-box;
         min-width: 100%;
        width: 100%; /* Set the width to 100% for the printed page */
      }
      .print-bottom {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background-color: white; /* Set the background color if needed */
        /* Add any other styles you want for the print-bottom section */
      }
    `,
  });

  const handleDownload = async () => {
    var element = document.getElementById("device-details");
    var opt = {
      margin: 1,
      filename: "device.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    // Promise-based usage
    html2pdf().set(opt).from(element).save();
  };

  if (!deviceData) {
    return (
      <div className="h-[400px] flex justify-center items-center">
        <div className="loader h-5 w-5 border-2 border-t-transparent border-gray-400 rounded-full animate-spin inline-block"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center bg-bgBlue py-6 md:py-16 px-4 sm:px-8 ">
      <div className="flex flex-col md:flex-row mb-4 space-y-4 md:space-y-0 md:space-x-4">
        <button
          id="device_print-btn"
          onClick={handlePrint}
          className="bg-bluePrimary text-white rounded-md px-4 py-3 font-bold flex items-center w-full md:w-auto"
        >
          <PrinterIcon className="w-4 mr-2" /> <span>Print</span>
        </button>
        <button
          onClick={handleDownload}
          className="border border-bluePrimary text-bluePrimary rounded-md px-4 py-3 font-bold flex items-center w-full md:w-auto"
        >
          <ArrowDownTrayIcon className="w-4 mr-2" /> <span>Download</span>
        </button>
        <a
          href={`/devices/form?next_unique_id=${nextUniqueId}`}
          className="border border-bluePrimary text-bluePrimary rounded-md px-4 py-3 font-bold"
        >
          Register Device +
        </a>
      </div>
      <div
        ref={componentRef}
        id="device-details"
        className="md:max-w-[620px] w-full bg-white border-t-bluePrimary border-t-[20px] py-6 px-6 overflow-scroll lg:overflow-hidden"
      >
        <div className="flex items-center mb-4  space-y-4 md:space-y-0 md:space-x-4">
          {deviceData.brandLogoUrl && (
            <div className="w-1/4 md:mb-0">
              <img
                src={deviceData.brandLogoUrl}
                alt="brand-logo"
                className="w-full"
              />
            </div>
          )}
          {!deviceData.brand_logo && (
            <div className="!mt-0 w-1/4">
              <h3 className="uppercase font-bold text-4xl text-center">
                {deviceData.brand_name}
              </h3>
            </div>
          )}
          <div className="w-3/4">
            <h3 className="!mt-0 uppercase font-bold text-4xl text-right">
              {deviceData.model}
            </h3>
          </div>
        </div>
        <h4 className="text-center text-green-500 font-bold text-7xl">
          {deviceData.unique_id}
        </h4>
        <div className="text-center mb-8">
          <h3 className="uppercase text-lightRed font-bold text-7xl">
            {deviceData.serial_no?.replace(/\s+/g, "") ?? ""}
          </h3>
        </div>
        <div className="flex justify-center mb-8">
          <QRCode value={deviceData.unique_id} className="device-qr-code" />
        </div>
        <div className="border border-black border-l-bluePrimary border-l-8 p-4">
          <div className="mb-4">
            {["counter_color", "counter_black", "total_counter"].map((key) =>
              deviceData[key] > 0 ? (
                <p
                  key={key}
                  className="uppercase text-sm md:text-md mb-2 text-black"
                >
                  {key.replace("_", " ")}: {deviceData[key]}
                </p>
              ) : null
            )}
          </div>
          <div className="flex justify-between mb-4">
            {deviceData.device_options && (
              <p className="uppercase text-sm md:text-md flex text-black">
                Options:{" "}
                {[
                  { key: "finisher", label: "finisher" },
                  { key: "rt_lct", label: "rt/lct" },
                  { key: "cabinet", label: "cabinet" },
                  { key: "no_df", label: "no df" },
                  { key: "green", label: "green" },
                  { key: "blue", label: "blue" },
                ]
                  .filter((opt) => deviceData.device_options[opt.key] === 1)
                  .map((opt) => opt.label)
                  .join(", ") || "-"}
              </p>
            )}
          </div>
          <div className="flex justify-between mb-4">
            <p className="uppercase text-sm md:text-md flex text-black">
              trays: {deviceData.device_options?.trays}
            </p>
          </div>
          {deviceData.device_options?.spare_parts && (
            <p className="uppercase text-sm md:text-md flex text-black">
              Spare parts:{" "}
              {dayjs
                .utc(deviceData.device_options.spare_parts)
                .format("DD/MM/YYYY")}
            </p>
          )}
          {deviceData.device_options?.recycled_date && (
            <p className="uppercase text-sm md:text-md flex text-black">
              Recycled date:{" "}
              {dayjs
                .utc(deviceData.device_options.recycled_date)
                .format("DD/MM/YYYY")}
            </p>
          )}

          {deviceData.palette > 0 && (
            <p className="uppercase text-sm md:text-md text-black">
              Palette: {deviceData.palette}
            </p>
          )}

          {deviceData.comment && (
            <p className="uppercase text-sm md:text-md text-black">
              Comment: {deviceData.comment}
            </p>
          )}
          <div className="flex justify-between">
            <p className="uppercase text-sm md:text-md text-black">
              {deviceData.updated_at
                ? "Last edited by: "
                : "Created by: "}
              {deviceData.user &&
                deviceData.user.name?.[0] +
                  "." +
                  (deviceData.user.last_name?.[0] || "")}
            </p>
          </div>
        </div>

        <div className="flex print-bottom justify-center items-center pt-8">
          <img
            src={Ritex}
            alt="Company Logo"
            width="100px"
            height="100px"
            className="mr-10 object-cover"
          />
          <a href="https://www.aritex.ch/">www.aritex.ch</a>
        </div>
      </div>
    </div>
  );
}

export default DeviceDetails;
